import React, { ReactNode, useState } from 'react';
import { Alert, Button, Chip, Grid, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Add, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { KeyedMutator } from 'swr/_internal';
import { AccountData, AccountService, RefundReasonData } from '../api';
import { ApiError } from '../api/core/ApiError';
import { useSafeExecute } from '../common/apiNotifications/safeExecute';
import { usePaymentCapabilities } from '../common/payments/usePaymentCapabilities';

type RefundReasonsSettingsProps = {
  account: AccountData;
  updateLocalAccount: KeyedMutator<AccountData>;
  setIsUpdatedSnackOpen: (open: boolean) => void;
};

type RefundReasonRow = {
  key: string;
  id?: string;
  label: string;
};

type CapabilityTooltipProps = {
  supportsRefunds: boolean;
  fillWidth?: boolean;
  children: ReactNode;
};

const rowsFromAccount = (account: AccountData): RefundReasonRow[] =>
  (account?.refund_reasons ?? []).map((reason: RefundReasonData) => ({
    key: reason.id ?? reason.label,
    id: reason.id,
    label: reason.label,
  }));

const CapabilityTooltip = ({ supportsRefunds, fillWidth, children }: CapabilityTooltipProps) => {
  const { t } = useTranslation();
  return (
    <Tooltip title={supportsRefunds ? '' : t('paymentGate.unsupported')} enterTouchDelay={0} arrow>
      <Grid component="span" sx={{ display: 'inline-flex', flex: fillWidth ? 1 : undefined }}>
        {children}
      </Grid>
    </Tooltip>
  );
};

export const RefundReasonsSettings = ({
  account,
  updateLocalAccount,
  setIsUpdatedSnackOpen,
}: RefundReasonsSettingsProps) => {
  const { t } = useTranslation();
  const safeExecute = useSafeExecute();
  const { refunds: supportsRefunds } = usePaymentCapabilities();
  const [rows, setRows] = useState<RefundReasonRow[]>(() => rowsFromAccount(account));
  const [newRowCounter, setNewRowCounter] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const trimmedLabels = rows.map((row) => row.label.trim().toLowerCase());
  const isBlank = (row: RefundReasonRow) => row.label.trim() === '';
  const isDuplicate = (row: RefundReasonRow) =>
    trimmedLabels.filter((label) => label === row.label.trim().toLowerCase()).length > 1;
  const hasBlankLabel = trimmedLabels.some((label) => label === '');
  const hasDuplicateLabel = new Set(trimmedLabels).size !== trimmedLabels.length;

  const addRow = () => {
    setRows((current) => [...current, { key: `new-${newRowCounter}`, label: '' }]);
    setNewRowCounter((current) => current + 1);
  };

  const updateLabel = (key: string, label: string) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, label } : row)));

  const deleteRow = (key: string) => setRows((current) => current.filter((row) => row.key !== key));

  const save = async () => {
    setIsSaving(true);
    try {
      const updatedAccount: AccountData = await safeExecute({
        apiCall: () =>
          AccountService.updateRefundReasons({
            refund_reasons: rows.map((row) => ({ id: row.id, label: row.label.trim() })),
          }),
        successMessage: t('refunds.settings.saved'),
        onErrorMessageGenerator: (error: ApiError) => {
          const detail = error.body?.detail;
          return typeof detail === 'string' ? detail : t('api.error.default');
        },
      });
      await updateLocalAccount(updatedAccount);
      setRows(rowsFromAccount(updatedAccount));
      setIsUpdatedSnackOpen(true);
    } catch {
      return;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Grid container flexDirection="column" flexWrap="nowrap" gap={2} maxWidth={600}>
      <Grid container flexDirection="column">
        <Grid container alignItems="center" gap={1}>
          <Typography variant="h6">{t('refunds.settings.title')}</Typography>
          {supportsRefunds && <Chip size="small" color="success" label={t('paymentGate.supported')} />}
        </Grid>
        <Typography variant="caption" color="text.secondary">
          {t('refunds.settings.description')}
        </Typography>
      </Grid>
      {!supportsRefunds && <Alert severity="info">{t('refunds.settings.providerUnsupportedAlert')}</Alert>}
      {rows.map((row) => (
        <Grid key={row.key} container alignItems="center" gap={1} wrap="nowrap">
          <CapabilityTooltip supportsRefunds={supportsRefunds} fillWidth>
            <TextField
              size="small"
              fullWidth
              disabled={!supportsRefunds}
              label={t('refunds.settings.reasonName')}
              value={row.label}
              onChange={(event) => updateLabel(row.key, event.target.value)}
              error={isBlank(row) || isDuplicate(row)}
              helperText={
                isBlank(row)
                  ? t('refunds.settings.reasonRequired')
                  : isDuplicate(row)
                  ? t('refunds.settings.reasonsDifferent')
                  : undefined
              }
            />
          </CapabilityTooltip>
          <CapabilityTooltip supportsRefunds={supportsRefunds}>
            <IconButton aria-label={t('delete')} disabled={!supportsRefunds} onClick={() => deleteRow(row.key)}>
              <Delete />
            </IconButton>
          </CapabilityTooltip>
        </Grid>
      ))}
      <Typography variant="caption" color="text.secondary">
        {t('refunds.settings.deleteHint')}
      </Typography>
      <Grid container justifyContent="space-between" alignItems="center">
        <CapabilityTooltip supportsRefunds={supportsRefunds}>
          <Button startIcon={<Add />} disabled={!supportsRefunds} onClick={addRow}>
            {t('refunds.settings.add')}
          </Button>
        </CapabilityTooltip>
        <CapabilityTooltip supportsRefunds={supportsRefunds}>
          <LoadingButton
            variant="contained"
            loading={isSaving}
            disabled={!supportsRefunds || rows.length === 0 || hasBlankLabel || hasDuplicateLabel}
            onClick={save}
            sx={{ boxShadow: 'none' }}>
            {t('save')}
          </LoadingButton>
        </CapabilityTooltip>
      </Grid>
    </Grid>
  );
};
